import React, {
  useEffect, useMemo, useReducer, useRef,
} from 'react';
import { Autocomplete, Chip } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useRequest } from 'shared/hooks';
import Select from './index';

// Only do search calls this often.
const debounceIntervalMS = 250;

// Limit backend searches to this many and filter client side when there are fewer results.
// This number is small because every keypress renders every option.
const maxItems = 1000;

function makeSearch(queryKey, query, baseSearch, routeParam) {
  if (routeParam) {
    return routeParam;
  }
  const sp = new URLSearchParams(baseSearch);
  sp.set('l', maxItems.toString());
  if (query) sp.set(queryKey, query);
  return `?${sp.toString()}`;
}

const initialState = {
  inputValue: undefined, // Value in searchable select input.
  options: [], // Options from the last backend search result.
};

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'reset':
      return initialState;
    case 'options':
      return { // Runs when new options come from backend.
        ...state,
        options: payload.rows ? payload.rows : payload,
      };
    case 'input': {
      const {
        newInputValue,
      } = payload;

      return {
        ...state,
        inputValue: newInputValue,
      };
    }
    default:
      return state;
  }
}

/**
 * Async searchable select component that loads options from a backend search.
 * @param {Object} props
 * @param {string} [props.baseSearch='']
 * @param {boolean} [props.creatable=false]
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.disableClearable=false]
 * @param {Function} [props.getOptionLabel=(o) => o?.name]
 * @param {Function} [props.getOptionValue=(o) => o?.id]
 * @param {string} props.label
 * @param {Function} props.onChange
 * @param {Function} [props.onCreate=() => null]
 * @param {boolean} [props.preloadOptions=true]
 * @param {string} [props.queryKey='q'] - queryKey changes the key that is used for backend
 * filtering. Useful when a select should filter more specifically, as the 'q' param is usually
 * generic.
 * @param {Function} props.searchRequest
 * @param {Object<string, *>} [props.selectedOption=null]
 * @param {number} [props.updateCount=0]
 * @param {string} [props.placeholder='Select an option...']
 */
export function AsyncSearchableSelect(props) {
  const {
    baseSearch = '',
    creatable = false,
    disabled = false,
    disableClearable = false,
    getOptionLabel = (o) => o?.name,
    getOptionValue = (o) => o?.id,
    label,
    id,
    onChange,
    onCreate = () => null,
    preloadOptions = true,
    queryKey = 'q',
    searchRequest,
    selectedOption = null,
    updateCount = 0,
    placeholder = 'Select an option...',
  } = props;

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    options,
    inputValue,
  } = state;

  const timeout = useRef(null);
  const { request } = useRequest();

  // EntityPicker clears the selection with null, which must reset the options too.
  useEffect(() => {
    if (updateCount || selectedOption === null) dispatch({ action: 'reset' });
  }, [selectedOption, updateCount]);

  useEffect(() => {
    dispatch({ action: 'reset' });
  }, [searchRequest]);

  // Load options from backend.
  useEffect(() => {
    (async () => {
      if (!preloadOptions && !inputValue) {
        // CCancel any previous request still pending.
        await request(null, '', true);
        dispatch({
          action: 'options',
          payload: [{
            name: 'Type to begin searching...',
            disabled: true,
            staged: false,
          }],
        });
        return;
      }
      clearTimeout(timeout.current);
      timeout.current = setTimeout(async () => {
        const result = await request(
          searchRequest(makeSearch(queryKey, inputValue, baseSearch)),
          '',
          true,
        );
        if (result) {
          dispatch({
            action: 'options',
            payload: result,
          });
        }
      }, debounceIntervalMS);
    })();
    return () => clearTimeout(timeout.current);
  }, [
    request,
    queryKey,
    inputValue,
    searchRequest,
    baseSearch,
    updateCount,
    preloadOptions,
  ]);

  const setInputValue = (value) => {
    dispatch({
      action: 'input',
      payload: { newInputValue: value },
    });
  };

  return (
    <Select
      key={`${label}-${id}`}
      creatable={creatable}
      disabled={disabled}
      disableClearable={disableClearable}
      label={label}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      inputValue={inputValue}
      onChange={onChange}
      onCreate={onCreate}
      setInputValue={setInputValue}
      options={options}
      selectedOption={selectedOption}
      placeholder={placeholder}
    />
  );
}

/**
 * Dependent async searchable select component that disables until a dependent ID is provided.
 * @param {Object} props
 * @param {string} props.label
 * @param {string|number} [props.dependentId]
 * @param {Function} props.searchRequest - Called with `dependentId`. May be redefined every
 *   render by the caller; only `dependentId` controls when options reload (see below).
 */
export function DependentAsyncSearchableSelect(props) {
  const {
    label,
    dependentId,
    searchRequest,
    ...rest
  } = props;

  // A ref, so `search` changes only with `dependentId`. Callers pass a fresh closure each
  // render (`getWlan(vszId)`), which would otherwise refetch in a loop.
  const searchRequestRef = useRef(searchRequest);
  searchRequestRef.current = searchRequest;

  const search = useMemo(() => () => {
    if (dependentId) {
      return searchRequestRef.current(dependentId);
    }
    return null;
  }, [dependentId]);

  return (
    <AsyncSearchableSelect
      label={label}
      disabled={!dependentId}
      searchRequest={search}
      {...rest}
    />
  );
}

/**
 * Static searchable select component that filters from a static list of options.
 * @param {Object} props
 * @param {boolean} [props.disableClearable=false]
 * @param {Function} [props.getOptionLabel=(o) => o.label]
 * @param {Function} [props.getOptionValue=(o) => o.value]
 * @param {string} props.label
 * @param {Function} props.onChange
 * @param {string} [props.placeholder='Select an option...']
 * @param {Array} props.staticOptions
 * @param {string|number|Object} [props.selectedOption=null]
 */
export function StaticSearchableSelect(props) {
  const {
    disableClearable = false,
    getOptionLabel = (o) => o.label,
    getOptionValue = (o) => o.value,
    label,
    onChange,
    placeholder = 'Select an option...',
    staticOptions,
    selectedOption = null,
    ...rest
  } = props;

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    options,
    inputValue,
  } = state;

  useEffect(() => {
    dispatch({
      action: 'options',
      payload: staticOptions,
    });
  }, [staticOptions]);

  const setInputValue = (value) => {
    dispatch({
      action: 'input',
      payload: { newInputValue: value },
    });
  };

  return (
    <Select
      disableClearable={disableClearable}
      label={label}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      inputValue={inputValue}
      onChange={onChange}
      setInputValue={setInputValue}
      options={options}
      placeholder={placeholder}
      selectedOption={selectedOption}
      {...rest}
    />
  );
}

/**
 * Async multiple select component that allows selecting multiple options from backend
 * search results.
 * @param {Object} props
 * @param {string} [props.baseSearch='']
 * @param {string} [props.routeParam='']
 * @param {Function} [props.getOptionLabel=(o) => o.name]
 * @param {Function} [props.getOptionValue=(o) => o.id]
 * @param {string} props.label
 * @param {Function} props.onChange
 * @param {Function} [props.onBlur=() => null]
 * @param {boolean} [props.preloadOptions=true]
 * @param {string} [props.queryKey='q'] - queryKey changes the key used for backend
 *   filtering. Useful when a select should filter more specifically, as the 'q' param is
 *   usually generic.
 * @param {Function} props.searchRequest
 * @param {Array<*>} [props.selectedOptions=[]]
 * @param {number} [props.updateCount=0]
 * @param {boolean} [props.disabled=false]
 * @param {Function} [props.getOptionDisabled=() => null]
 * @param {string} [props.placeholder='Select an option...']
 */
export function AsyncMultipleSelect({
  baseSearch = '',
  routeParam = '',
  getOptionLabel = (o) => o.name,
  getOptionValue = (o) => o.id,
  label,
  onChange,
  onBlur = () => null,
  preloadOptions = true,
  queryKey = 'q',
  searchRequest,
  selectedOptions = [],
  updateCount = 0,
  disabled = false,
  getOptionDisabled = () => null,
  placeholder = 'Select an option...',
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    options,
    inputValue,
  } = state;

  const timeout = useRef(null);
  const { request } = useRequest();

  useEffect(() => {
    if (updateCount) dispatch({ action: 'reset' });
  }, [updateCount]);

  useEffect(() => {
    dispatch({ action: 'reset' });
  }, [searchRequest]);

  // Load options from backend.
  useEffect(() => {
    (async () => {
      if (!preloadOptions && !inputValue) {
        // Cancel any previous request still pending.
        await request(null, '', true);
        dispatch({
          action: 'options',
          payload: [{
            name: 'Type to begin searching...',
            disabled: true,
            staged: false,
          }],
        });
        return;
      }
      clearTimeout(timeout.current);
      timeout.current = setTimeout(async () => {
        const search = makeSearch(queryKey, inputValue, baseSearch, routeParam);
        const result = await request(searchRequest(search), '', true);
        if (result) {
          dispatch({
            action: 'options',
            payload: result,
          });
        }
      }, debounceIntervalMS);
    })();
    return () => clearTimeout(timeout.current);
  }, [
    request,
    queryKey,
    inputValue,
    searchRequest,
    baseSearch,
    updateCount,
    preloadOptions,
    routeParam,
  ]);

  const setInputValue = (value) => {
    dispatch({
      action: 'input',
      payload: { newInputValue: value },
    });
  };

  return (
    <Autocomplete
      getOptionDisabled={getOptionDisabled}
      disabled={disabled}
      multiple
      disableCloseOnSelect
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionValue}
      filterSelectedOptions
      value={selectedOptions}
      isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onBlur={onBlur}
      onChange={(event, newValue) => onChange(newValue)}
      renderValue={(value, getItemProps) => value.map((option, index) => {
        const {
          key,
          ...itemProps
        } = getItemProps({ index });
        return (
          <Chip sx={{ height: '22px' }} key={key} label={getOptionLabel(option)} {...itemProps} />
        );
      })}
      renderOption={(props, option) => (
        <li {...props} key={getOptionValue(option) || option.id}>
          {option.name || getOptionLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          className="input-field"
          {...params}
          slotProps={{
            ...params.slotProps,
            inputLabel: { ...params.slotProps.inputLabel, shrink: true },
          }}
          label={label}
          size="small"
          placeholder={selectedOptions?.length > 0 ? null : placeholder}
        />
      )}
    />
  );
}

export function DependentAsyncMultipleSelect(props) {
  const {
    label,
    dependentId,
    searchRequest,
    ...rest
  } = props;

  // Keep the latest searchRequest in a ref so the `search` identity does NOT depend on it.
  // Callers commonly pass a freshly-built closure each render (e.g. `getWlan(vszId)`); if that
  // identity flowed into `search`, the child AsyncSearchableSelect's searchRequest-keyed effects
  // would reset + refetch on every render in a loop. Here `dependentId` is the meaningful reload
  // signal, so `search` only changes when `dependentId` changes; the ref supplies the current
  // request at call time.
  const searchRequestRef = useRef(searchRequest);
  searchRequestRef.current = searchRequest;

  const search = useMemo((searchQuery) => () => {
    if (dependentId) {
      return searchRequestRef.current(dependentId, searchQuery || '');
    }
    return null;
  }, [dependentId]);

  return (
    <AsyncMultipleSelect
      label={label}
      searchRequest={search}
      disabled={!dependentId}
      {...rest}
    />
  );
}
